import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  MessageSquare, Users, Send, Pin, Trash2, Edit2, X, Check,
  UserPlus, UserMinus, MapPin, Music, Award, ChevronDown, ChevronUp, Flag, Lock,
  Heart, Reply, MoreHorizontal, Play, Pause, Upload, Bookmark,
  Clock, Eye, Headphones, ArrowUpDown, Trophy, Star, Globe, ListMusic, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import UpgradePrompt from '../components/UpgradePrompt';
import DJNetworkMap from '../components/DJNetworkMap';

// Import modular community components
import { 
  TierBadge, 
  AudioPlayer, 
  Comment, 
  NotificationBell, 
  DeleteModal, 
  MixUploadModal,
  TrackSharePost,
  TrackShareModal,
  MyPlaylistsTab,
  GrowthTracker,
  BrandingChallenges,
  ProfileAuditModal,
  ProfileAuditPost,
  PressKitBuilder,
  SuggestedConnections,
  DJ_REACTIONS,
  POST_TYPES,
  DJ_GENRES,
  SKILL_LEVELS,
  ROOM_ACCESS,
  MIX_FEEDBACK_TAGS
} from '../components/community';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Community = () => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState('general');
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postType, setPostType] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showProfile, setShowProfile] = useState(null);
  const [suggestedDJs, setSuggestedDJs] = useState([]);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: '', postId: '', commentId: '' });
  const [showMixUpload, setShowMixUpload] = useState(false);
  const [mixUploadLimits, setMixUploadLimits] = useState(null);
  const [commentSortBy, setCommentSortBy] = useState({}); // { postId: 'timestamp' | 'recent' }
  const [viewMode, setViewMode] = useState('rooms'); // 'rooms' or 'network-map'
  // Track Discovery state
  const [trackDiscoveryView, setTrackDiscoveryView] = useState('feed'); // 'feed' or 'playlists'
  const [showTrackShareModal, setShowTrackShareModal] = useState(false);
  const [userPlaylists, setUserPlaylists] = useState([]);
  const [trendingTracks, setTrendingTracks] = useState([]);
  // DJ Growth room state
  const [djGrowthView, setDjGrowthView] = useState('feed'); // 'feed' or 'growth-tracker' or 'challenges'
  const [showProfileAuditModal, setShowProfileAuditModal] = useState(false);
  const [profileForm, setProfileForm] = useState({
    dj_name: '',
    skill_level: '',
    genres: [],
    location: '',
    instagram_url: '',
    tiktok_url: '',
    soundcloud_url: ''
  });
  
  // Store refs for audio player seek functions per post
  const audioSeekRefs = useRef({});

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email === 'contact@blendonics.com';
  
  // Get effective tier
  const getEffectiveTier = () => {
    if (isAdmin) return 'elite';
    if (user?.membership_status === 'lifetime' && user?.has_course_access) return 'starter';
    if (user?.membership_status === 'active' || user?.membership_status === 'cancelling') {
      return user?.membership_tier || 'free';
    }
    return 'free';
  };
  
  const tier = getEffectiveTier();
  const canAccessCommunity = tier !== 'free';
  
  // Get rooms user can access
  const accessibleRooms = isAdmin ? 
    ['general', 'beginner-lab', 'local-connections', 'mix-feedback', 'dj-growth', 'track-discovery'] :
    ROOM_ACCESS[tier] || [];
  
  // Check if user can access a specific room
  const canAccessRoom = (roomId) => {
    if (isAdmin) return true;
    return accessibleRooms.includes(roomId);
  };

  const fetchRooms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch rooms', error);
    }
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/posts?room_id=${activeRoom}&sort_by=${sortBy}`);
      setPosts(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Upgrade to Pro membership to access this room');
      }
    }
  }, [activeRoom, sortBy]);

  const fetchSuggestedDJs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/suggested-djs`);
      setSuggestedDJs(response.data);
    } catch (error) {
      console.error('Failed to fetch suggested DJs', error);
    }
  }, []);

  const fetchMixUploadLimits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/mix-upload-limits`);
      setMixUploadLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch mix upload limits', error);
    }
  }, []);

  useEffect(() => {
    if (canAccessCommunity) {
      fetchRooms();
      fetchSuggestedDJs();
    }
  }, [user, canAccessCommunity, fetchRooms, fetchSuggestedDJs]);

  useEffect(() => {
    if (canAccessCommunity && activeRoom && canAccessRoom(activeRoom)) {
      fetchPosts();
    }
  }, [activeRoom, sortBy, canAccessCommunity, fetchPosts]);

  // Polling for real-time updates
  useEffect(() => {
    if (canAccessCommunity && activeRoom) {
      const interval = setInterval(fetchPosts, 15000); // Poll every 15 seconds
      return () => clearInterval(interval);
    }
  }, [canAccessCommunity, activeRoom, fetchPosts]);

  // Fetch mix upload limits when entering mix-feedback room
  useEffect(() => {
    if (activeRoom === 'mix-feedback' && canAccessRoom('mix-feedback')) {
      fetchMixUploadLimits();
    }
  }, [activeRoom, fetchMixUploadLimits]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/community/posts`, {
        content: newPost,
        room_id: activeRoom,
        post_type: postType
      });
      setNewPost('');
      setPostType('standard');
      fetchPosts();
      toast.success('Posted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setLoading(false);
    }
  };

  const handleMixUpload = async (formData) => {
    try {
      await axios.post(`${API}/community/mix-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchPosts();
      fetchMixUploadLimits();
      toast.success('Mix uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload mix');
      throw error;
    }
  };

  const handleReact = async (postId, emoji) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/react?emoji=${encodeURIComponent(emoji)}`);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const handleDeletePost = async (postId) => {
    setDeleteModal({ isOpen: true, type: 'post', postId, commentId: '' });
  };

  const confirmDeletePost = async () => {
    try {
      await axios.delete(`${API}/community/posts/${deleteModal.postId}`);
      fetchPosts();
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
    setDeleteModal({ isOpen: false, type: '', postId: '', commentId: '' });
  };

  const handleEditPost = async (postId) => {
    try {
      await axios.put(`${API}/community/posts/${postId}`, {
        content: editContent,
        room_id: activeRoom,
        post_type: 'standard'
      });
      setEditingPost(null);
      fetchPosts();
      toast.success('Post updated');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handlePinPost = async (postId) => {
    try {
      await axios.put(`${API}/admin/community/posts/${postId}/pin`);
      fetchPosts();
      toast.success('Post pin toggled');
    } catch (error) {
      toast.error('Failed to pin post');
    }
  };

  const handleToggleTopMix = async (postId) => {
    try {
      const response = await axios.put(`${API}/admin/community/posts/${postId}/top-mix`);
      fetchPosts();
      toast.success(response.data.is_top_mix ? '🏆 Marked as Top Mix of the Week!' : 'Removed from Top Mix');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Top Mix status');
    }
  };

  // ==========================================
  // TRACK DISCOVERY HANDLERS
  // ==========================================
  
  const fetchUserPlaylists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/playlists`);
      setUserPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Failed to fetch playlists');
    }
  }, []);

  const fetchTrendingTracks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/track-discovery/trending`);
      setTrendingTracks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch trending tracks');
    }
  }, []);

  const handleShareTrack = async (trackData) => {
    try {
      await axios.post(`${API}/community/track-share`, trackData);
      fetchPosts();
      toast.success('Track shared successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share track');
      throw error;
    }
  };

  const handleSaveToPlaylist = async (postId, playlistId = null) => {
    try {
      // If no playlistId, use default playlist
      let targetPlaylistId = playlistId;
      if (!targetPlaylistId) {
        // Fetch playlists if not loaded
        if (userPlaylists.length === 0) {
          const response = await axios.get(`${API}/playlists`);
          const playlists = response.data.playlists || [];
          setUserPlaylists(playlists);
          const defaultPlaylist = playlists.find(p => p.is_default);
          targetPlaylistId = defaultPlaylist?.id;
        } else {
          const defaultPlaylist = userPlaylists.find(p => p.is_default);
          targetPlaylistId = defaultPlaylist?.id;
        }
      }
      
      if (!targetPlaylistId) {
        toast.error('No playlist found');
        return;
      }
      
      await axios.post(`${API}/playlists/${targetPlaylistId}/tracks`, { post_id: postId });
      fetchPosts();
      fetchUserPlaylists();
      toast.success('Track saved to playlist!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save track');
    }
  };

  // Fetch playlists when entering track-discovery room
  useEffect(() => {
    if (activeRoom === 'track-discovery' && canAccessCommunity) {
      fetchUserPlaylists();
      fetchTrendingTracks();
    }
  }, [activeRoom, canAccessCommunity, fetchUserPlaylists, fetchTrendingTracks]);

  const handleFlagPost = async (postId) => {
    if (!window.confirm('Are you sure you want to flag this post for review by moderators?')) return;
    try {
      await axios.post(`${API}/community/posts/${postId}/flag?reason=inappropriate`);
      toast.success('Post flagged for review');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already flagged')) {
        toast.info('You already flagged this post');
      } else {
        toast.error('Failed to flag post');
      }
    }
  };

  // Comment handlers
  const handleAddComment = async (postId, content, timestampSeconds = null) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/comment`, { 
        content,
        timestamp_seconds: timestampSeconds
      });
      fetchPosts();
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      toast.success(timestampSeconds !== null ? 'Timestamped feedback added!' : 'Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Sort comments helper - for mix posts, can sort by timestamp
  const getSortedComments = (comments, postId, isMixPost) => {
    if (!comments || comments.length === 0) return [];
    
    const sortOrder = commentSortBy[postId] || 'recent';
    
    if (sortOrder === 'timestamp' && isMixPost) {
      // Sort by timestamp - comments with timestamps first (ascending), then others by date
      return [...comments].sort((a, b) => {
        const aHasTimestamp = a.timestamp_seconds !== null && a.timestamp_seconds !== undefined;
        const bHasTimestamp = b.timestamp_seconds !== null && b.timestamp_seconds !== undefined;
        
        if (aHasTimestamp && bHasTimestamp) {
          return a.timestamp_seconds - b.timestamp_seconds;
        }
        if (aHasTimestamp) return -1;
        if (bHasTimestamp) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
    
    // Default: sort by recent
    return [...comments].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
  };

  const toggleCommentSort = (postId) => {
    setCommentSortBy(prev => ({
      ...prev,
      [postId]: prev[postId] === 'timestamp' ? 'recent' : 'timestamp'
    }));
  };

  // Handler for timestamped feedback from AudioPlayer
  const handleAddTimestampedFeedback = async (postId, timestampSeconds, content) => {
    await handleAddComment(postId, content, timestampSeconds);
  };

  // Handler for seeking to timestamp when clicking a comment's timestamp badge
  const handleSeekToTimestamp = (postId, timestampSeconds) => {
    const seekRef = audioSeekRefs.current[postId];
    if (seekRef && typeof seekRef === 'function') {
      seekRef(timestampSeconds);
    }
  };

  const handleReplyComment = async (postId, parentCommentId, content) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/comment`, { 
        content,
        parent_comment_id: parentCommentId
      });
      fetchPosts();
      toast.success('Reply added!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reply');
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    setDeleteModal({ isOpen: true, type: 'comment', postId, commentId });
  };

  const confirmDeleteComment = async () => {
    try {
      await axios.delete(`${API}/community/posts/${deleteModal.postId}/comments/${deleteModal.commentId}`);
      fetchPosts();
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
    setDeleteModal({ isOpen: false, type: '', postId: '', commentId: '' });
  };

  const handleEditComment = async (postId, commentId, content) => {
    try {
      await axios.put(`${API}/community/posts/${postId}/comments/${commentId}`, { content });
      fetchPosts();
      toast.success('Comment updated');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleLikeComment = async (postId, commentId) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/comments/${commentId}/like`);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to like comment');
    }
  };

  const handleMixPlay = async (postId) => {
    try {
      await axios.post(`${API}/community/posts/${postId}/play`);
    } catch (error) {
      // Silent fail
    }
  };

  const handleFollow = async (userId) => {
    try {
      const response = await axios.post(`${API}/community/follow/${userId}`);
      toast.success(response.data.following ? 'Following!' : 'Unfollowed');
      fetchSuggestedDJs();
      if (showProfile) {
        fetchProfile(userId);
      }
    } catch (error) {
      toast.error('Failed to follow');
    }
  };

  const fetchProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/community/profile/${userId}`);
      setShowProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/community/profile`, profileForm);
      toast.success('Profile updated!');
      setShowProfileSetup(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const getReactionCount = (reactions, emoji) => {
    return reactions?.[emoji]?.length || 0;
  };

  const hasReacted = (reactions, emoji) => {
    return reactions?.[emoji]?.includes(user?.id) || false;
  };

  const getActiveRoomInfo = () => {
    return rooms.find(r => r.id === activeRoom) || { name: 'General', description: '' };
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  // Show upgrade prompt for free users
  if (!canAccessCommunity) {
    return <UpgradePrompt feature="community" currentTier={tier} requiredTier="starter" />;
  }

  return (
    <div className="min-h-screen py-8" data-testid="community-page">
      <div className="container mx-auto px-4">
        {/* View Toggle Header */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <button
            onClick={() => setViewMode('rooms')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              viewMode === 'rooms'
                ? 'bg-[#D4AF37] text-black'
                : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
            }`}
            data-testid="view-rooms-btn"
          >
            <MessageSquare size={16} />
            Chat Rooms
          </button>
          <button
            onClick={() => setViewMode('network-map')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              viewMode === 'network-map'
                ? 'bg-[#D4AF37] text-black'
                : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
            }`}
            data-testid="view-network-map-btn"
          >
            <Globe size={16} />
            DJ Network Map
          </button>
        </div>

        {/* Network Map View */}
        {viewMode === 'network-map' ? (
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">DJ Network Map</h1>
              <p className="text-muted-foreground">Visualize your connections with DJs worldwide</p>
            </div>
            <DJNetworkMap />
          </div>
        ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          
          {/* Left Sidebar - Rooms */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="glass-panel rounded-2xl p-4 border sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold flex items-center gap-2">
                  <MessageSquare size={18} style={{ color: '#D4AF37' }} />
                  Rooms
                </h2>
                <NotificationBell />
              </div>
              <div className="space-y-2">
                {rooms.map((room) => {
                  const roomAccessible = canAccessRoom(room.id);
                  const roomTier = room.min_tier || 'starter';
                  const requiredTier = roomTier === 'pro' ? 'Pro+' : roomTier === 'elite' ? 'Elite' : 'Starter+';
                  return (
                    <button
                      key={room.id}
                      onClick={() => roomAccessible && setActiveRoom(room.id)}
                      disabled={!roomAccessible}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeRoom === room.id 
                          ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/50' 
                          : roomAccessible 
                            ? 'hover:bg-white/5' 
                            : 'opacity-50 cursor-not-allowed'
                      }`}
                      data-testid={`room-${room.id}`}
                    >
                      <span className="block font-medium">{room.name}</span>
                      {!roomAccessible && (
                        <span className="text-xs flex items-center gap-1" style={{ color: '#D4AF37' }}>
                          <Lock size={10} /> {requiredTier} Required
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Profile Setup Button */}
              <div className="mt-6 pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => {
                    setProfileForm({
                      dj_name: user?.dj_name || '',
                      skill_level: user?.skill_level || '',
                      genres: user?.genres || [],
                      location: user?.location || '',
                      instagram_url: user?.instagram_url || '',
                      tiktok_url: user?.tiktok_url || '',
                      soundcloud_url: user?.soundcloud_url || ''
                    });
                    setShowProfileSetup(true);
                  }}
                >
                  <Users size={14} className="mr-2" />
                  Edit DJ Profile
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content - Posts */}
          <div className="flex-1 max-w-2xl">
            {/* Room Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">{getActiveRoomInfo().name}</h1>
                <p className="text-sm text-muted-foreground">{getActiveRoomInfo().description}</p>
              </div>
              
              {/* Sort Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSortBy(sortBy === 'recent' ? 'popular' : 'recent')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors"
                  style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                >
                  <ArrowUpDown size={14} />
                  {sortBy === 'recent' ? 'Recent' : 'Popular'}
                </button>
              </div>
            </div>

            {/* Track Discovery Sub-Navigation */}
            {activeRoom === 'track-discovery' && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={() => setTrackDiscoveryView('feed')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      trackDiscoveryView === 'feed'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="track-feed-tab"
                  >
                    <Music size={16} />
                    Track Feed
                  </button>
                  <button
                    onClick={() => setTrackDiscoveryView('playlists')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      trackDiscoveryView === 'playlists'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="my-playlists-tab"
                  >
                    <ListMusic size={16} />
                    My Playlists
                  </button>
                </div>
                
                {/* Trending Section (only in feed view) */}
                {trackDiscoveryView === 'feed' && trendingTracks.length > 0 && (
                  <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🔥</span>
                      <span className="font-semibold" style={{ color: '#D4AF37' }}>Trending This Week</span>
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {trendingTracks.slice(0, 5).map((track) => (
                        <div 
                          key={track.id}
                          className="flex-shrink-0 p-2 rounded-lg bg-white/5 flex items-center gap-2 min-w-[200px]"
                        >
                          <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center overflow-hidden">
                            {track.track_artwork ? (
                              <img src={track.track_artwork} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Music size={16} className="text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{track.track_title || 'Unknown'}</p>
                            <p className="text-xs text-gray-400 truncate">{track.track_artist || 'Unknown'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DJ Growth Sub-Navigation */}
            {activeRoom === 'dj-growth' && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <button
                    onClick={() => setDjGrowthView('feed')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      djGrowthView === 'feed'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="growth-feed-tab"
                  >
                    <MessageSquare size={16} />
                    Discussion
                  </button>
                  <button
                    onClick={() => setDjGrowthView('growth-tracker')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      djGrowthView === 'growth-tracker'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="growth-tracker-tab"
                  >
                    <Award size={16} />
                    Growth Tracker
                  </button>
                  <button
                    onClick={() => setDjGrowthView('challenges')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      djGrowthView === 'challenges'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="challenges-tab"
                  >
                    <Trophy size={16} />
                    Challenges
                  </button>
                  <button
                    onClick={() => setDjGrowthView('press-kit')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      djGrowthView === 'press-kit'
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-white/5 border border-gray-600 hover:border-[#D4AF37]'
                    }`}
                    data-testid="press-kit-tab"
                  >
                    <FileText size={16} />
                    Press Kit
                  </button>
                </div>
              </div>
            )}

            {/* Show Growth Tracker Tab for DJ Growth */}
            {activeRoom === 'dj-growth' && djGrowthView === 'growth-tracker' ? (
              <GrowthTracker user={user} />
            ) : activeRoom === 'dj-growth' && djGrowthView === 'challenges' ? (
              <BrandingChallenges user={user} isAdmin={isAdmin} />
            ) : activeRoom === 'dj-growth' && djGrowthView === 'press-kit' ? (
              <PressKitBuilder user={user} />
            ) : activeRoom === 'track-discovery' && trackDiscoveryView === 'playlists' ? (
              <MyPlaylistsTab user={user} />
            ) : (
              <>
                {/* Create Post */}
                <div className="glass-panel rounded-2xl p-4 border mb-6">
                  <form onSubmit={handleCreatePost}>
                    <Textarea
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      placeholder="Share something with the community..."
                      rows={3}
                      className="mb-3"
                      data-testid="post-input"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <select
                          value={postType}
                          onChange={(e) => setPostType(e.target.value)}
                          className="px-3 py-1 rounded-lg text-sm border border-border bg-background"
                        >
                          {Object.entries(POST_TYPES).filter(([key]) => 
                            key !== 'admin' || isAdmin
                          ).filter(([key]) => 
                            key !== 'mix_upload' && key !== 'track_share'
                          ).map(([key, val]) => (
                            <option key={key} value={key}>{val.icon} {val.label}</option>
                          ))}
                        </select>
                        
                        {/* Mix Upload Button (only in mix-feedback room) */}
                        {activeRoom === 'mix-feedback' && (
                          <Button 
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowMixUpload(true)}
                            className="rounded-full"
                            style={{ borderColor: '#D4AF37', color: '#D4AF37' }}
                          >
                            <Headphones size={14} className="mr-2" />
                            Upload Mix
                          </Button>
                        )}
                        
                        {/* Share Track Button (only in track-discovery room) */}
                        {activeRoom === 'track-discovery' && (
                          <Button 
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowTrackShareModal(true)}
                            className="rounded-full"
                            style={{ borderColor: '#1DB954', color: '#1DB954' }}
                            data-testid="share-track-btn"
                          >
                            <Music size={14} className="mr-2" />
                            Share Track
                          </Button>
                        )}
                        
                        {/* Request Audit Button (only in dj-growth room feed view) */}
                        {activeRoom === 'dj-growth' && djGrowthView === 'feed' && (
                          <Button 
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowProfileAuditModal(true)}
                            className="rounded-full"
                            style={{ borderColor: '#a855f7', color: '#a855f7' }}
                            data-testid="request-audit-btn"
                          >
                            <Flag size={14} className="mr-2" />
                            Request Audit
                          </Button>
                        )}
                      </div>
                      <Button 
                        type="submit" 
                        disabled={loading || !newPost.trim()}
                        className="rounded-full"
                        style={{ background: '#D4AF37', color: '#141414' }}
                        data-testid="submit-post-btn"
                      >
                        <Send size={16} className="mr-2" />
                        Post
                      </Button>
                    </div>
                  </form>
                </div>

                {/* Posts Feed */}
                <div className="space-y-4">
                  {posts.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No posts in this room yet. Be the first to post!</p>
                    </div>
                  ) : (
                posts.map((post) => (
                  // Special rendering for track_share posts
                  post.post_type === 'track_share' ? (
                    <TrackSharePost
                      key={post.id}
                      post={post}
                      user={user}
                      isAdmin={isAdmin}
                      onReact={handleReact}
                      onComment={() => setExpandedComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                      onSaveToPlaylist={(postId) => handleSaveToPlaylist(postId)}
                      onExpandComments={(postId) => setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }))}
                      isTrending={trendingTracks.some(t => t.id === post.id)}
                    />
                  ) : post.post_type === 'profile_audit' ? (
                    // Special rendering for profile_audit posts
                    <ProfileAuditPost
                      key={post.id}
                      post={post}
                      onReact={handleReact}
                      onComment={() => setExpandedComments(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                      onExpandComments={(postId) => setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }))}
                    />
                  ) : (
                  <div 
                    key={post.id} 
                    className={`glass-panel rounded-2xl p-4 border transition-all ${
                      post.pinned ? 'border-[#D4AF37]/50' : ''
                    } ${post.user_tier === 'elite' ? 'ring-1 ring-purple-500/30' : ''} ${
                      post.is_top_mix ? 'ring-2 ring-yellow-500/50 border-yellow-500/50' : ''
                    }`}
                    data-testid={`post-${post.id}`}
                  >
                    {/* Top Mix Banner */}
                    {post.is_top_mix && (
                      <div 
                        className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4 -mt-1 -mx-1"
                        style={{ background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(251, 191, 36, 0.1))', border: '1px solid rgba(234, 179, 8, 0.3)' }}
                      >
                        <Trophy size={18} className="text-yellow-500" />
                        <span className="text-sm font-bold text-yellow-500">🏆 Top Mix of the Week</span>
                        <Star size={14} className="text-yellow-500 ml-auto" fill="currentColor" />
                      </div>
                    )}
                    
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => fetchProfile(post.user_id)}
                      >
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            post.author_tier === 'elite' || post.user_tier === 'elite' ? 'ring-2 ring-purple-500' : ''
                          }`}
                          style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
                        >
                          {(post.author_dj_name || post.user_name || 'U').charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold hover:underline" style={{ color: '#ffffff' }}>
                              {post.author_dj_name || post.user_name}
                            </p>
                            <TierBadge tier={post.author_tier || post.user_tier || 'starter'} />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {post.author_skill_level && `${post.author_skill_level} • `}
                            {new Date(post.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {post.pinned && (
                          <Pin size={14} style={{ color: '#D4AF37' }} />
                        )}
                        {post.post_type !== 'standard' && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                            {POST_TYPES[post.post_type]?.icon} {POST_TYPES[post.post_type]?.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mix Upload Player */}
                    {post.post_type === 'mix_upload' && post.mix_url && (
                      <div className="mb-4">
                        {/* Feedback Tags */}
                        {post.feedback_tags && post.feedback_tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {post.feedback_tags.map(tagId => {
                              const tag = MIX_FEEDBACK_TAGS.find(t => t.id === tagId);
                              return tag ? (
                                <span 
                                  key={tagId}
                                  className="text-xs px-2 py-1 rounded-full"
                                  style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
                                >
                                  {tag.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        
                        <AudioPlayer 
                          url={post.mix_url}
                          title={post.mix_title}
                          duration={post.mix_duration}
                          onPlay={() => handleMixPlay(post.id)}
                          onAddTimestampedFeedback={(timestamp, content) => handleAddTimestampedFeedback(post.id, timestamp, content)}
                          timestampedComments={post.comments?.filter(c => c.timestamp_seconds !== null && c.timestamp_seconds !== undefined) || []}
                          onSeekToTimestamp={{ current: null, setSeek: (fn) => { audioSeekRefs.current[post.id] = fn; } }}
                        />
                        
                        {/* Play stats */}
                        <div className="flex items-center gap-4 text-xs mb-2" style={{ color: '#666' }}>
                          <span className="flex items-center gap-1">
                            <Play size={12} /> {post.play_count || 0} plays
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye size={12} /> {post.view_count || 0} views
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Post Content */}
                    {editingPost === post.id ? (
                      <div className="mb-3">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={3}
                        />
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" onClick={() => handleEditPost(post.id)}>
                            <Check size={14} className="mr-1" /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingPost(null)}>
                            <X size={14} className="mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="mb-4 whitespace-pre-wrap" style={{ color: '#e0e0e0' }}>{post.content}</p>
                    )}

                    {/* Reactions */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {DJ_REACTIONS.map((emoji) => {
                        const count = getReactionCount(post.reactions, emoji);
                        const reacted = hasReacted(post.reactions, emoji);
                        return (
                          <button
                            key={emoji}
                            onClick={() => handleReact(post.id, emoji)}
                            className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-colors ${
                              reacted ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/50' : 'hover:bg-white/10 border border-transparent'
                            }`}
                          >
                            <span>{emoji}</span>
                            {count > 0 && <span className="text-xs">{count}</span>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Post Actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                      {/* Comment count toggle */}
                      <button 
                        onClick={() => toggleComments(post.id)}
                        className="flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-white/10 transition-colors"
                        style={{ color: '#a3a3a3' }}
                      >
                        <MessageSquare size={14} />
                        {post.comment_count || post.comments?.length || 0} Comments
                        {expandedComments[post.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      
                      {(post.user_id === user?.id || isAdmin) && (
                        <>
                          {post.user_id === user?.id && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => { setEditingPost(post.id); setEditContent(post.content); }}
                            >
                              <Edit2 size={14} className="mr-1" /> Edit
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeletePost(post.id)}
                            style={{ color: '#ef4444' }}
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </>
                      )}
                      {isAdmin && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handlePinPost(post.id)}
                          style={{ color: '#D4AF37' }}
                        >
                          <Pin size={14} className="mr-1" /> {post.pinned ? 'Unpin' : 'Pin'}
                        </Button>
                      )}
                      {isAdmin && post.post_type === 'mix_upload' && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleToggleTopMix(post.id)}
                          style={{ color: post.is_top_mix ? '#eab308' : '#a3a3a3' }}
                          title={post.is_top_mix ? 'Remove from Top Mix' : 'Mark as Top Mix of the Week'}
                        >
                          <Trophy size={14} className="mr-1" /> {post.is_top_mix ? 'Remove Top Mix' : 'Top Mix'}
                        </Button>
                      )}
                      {post.user_id !== user?.id && !isAdmin && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleFlagPost(post.id)}
                          style={{ color: '#a3a3a3' }}
                          title="Report this post"
                        >
                          <Flag size={14} className="mr-1" /> Report
                        </Button>
                      )}
                    </div>

                    {/* Comments Section */}
                    {expandedComments[post.id] && (
                      <div className="mt-4 pt-4 border-t border-border">
                        {/* Sort Toggle for Mix Posts */}
                        {post.post_type === 'mix_upload' && post.comments && post.comments.length > 1 && (
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs" style={{ color: '#666' }}>Sort comments:</span>
                            <button
                              onClick={() => toggleCommentSort(post.id)}
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors"
                              style={{ 
                                borderColor: commentSortBy[post.id] === 'timestamp' ? '#a855f7' : '#333',
                                color: commentSortBy[post.id] === 'timestamp' ? '#a855f7' : '#a3a3a3'
                              }}
                            >
                              <Clock size={12} />
                              {commentSortBy[post.id] === 'timestamp' ? 'By Timestamp' : 'By Recent'}
                            </button>
                          </div>
                        )}
                        
                        {/* Add Comment Input */}
                        <div className="flex gap-2 mb-4">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                            style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
                          >
                            {(user?.dj_name || user?.name || 'U').charAt(0)}
                          </div>
                          <Input
                            value={commentInputs[post.id] || ''}
                            onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                            placeholder="Write a comment..."
                            className="flex-1"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && commentInputs[post.id]?.trim()) {
                                handleAddComment(post.id, commentInputs[post.id]);
                              }
                            }}
                          />
                          <Button 
                            size="sm"
                            onClick={() => commentInputs[post.id]?.trim() && handleAddComment(post.id, commentInputs[post.id])}
                            disabled={!commentInputs[post.id]?.trim()}
                          >
                            <Send size={14} />
                          </Button>
                        </div>

                        {/* Comments List */}
                        <div className="space-y-0 max-h-96 overflow-y-auto">
                          {post.comments && post.comments.length > 0 ? (
                            getSortedComments(post.comments, post.id, post.post_type === 'mix_upload').map((comment) => (
                              <Comment
                                key={comment.id}
                                comment={comment}
                                postId={post.id}
                                user={user}
                                isAdmin={isAdmin}
                                onReply={handleReplyComment}
                                onDelete={handleDeleteComment}
                                onEdit={handleEditComment}
                                onLike={handleLikeComment}
                                onSeekToTimestamp={(timestamp) => handleSeekToTimestamp(post.id, timestamp)}
                                isMixPost={post.post_type === 'mix_upload'}
                              />
                            ))
                          ) : (
                            <p className="text-sm text-center py-4" style={{ color: '#666' }}>
                              No comments yet. Be the first to comment!
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  )
                ))
              )}
            </div>
            </>
            )}
          </div>

          {/* Right Sidebar - Suggested DJs */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="glass-panel rounded-2xl p-4 border sticky top-20">
              <SuggestedConnections onFollowUpdate={fetchSuggestedDJs} />
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowProfile(null)}>
          <div className="glass-panel rounded-2xl p-6 border max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div 
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${
                    showProfile.membership_tier === 'elite' ? 'ring-2 ring-purple-500' : ''
                  }`}
                  style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}
                >
                  {(showProfile.dj_name || showProfile.name || 'D').charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">{showProfile.dj_name || showProfile.name}</h3>
                    {showProfile.membership_tier && <TierBadge tier={showProfile.membership_tier} />}
                  </div>
                  <p className="text-sm text-muted-foreground">{showProfile.skill_level || 'DJ'}</p>
                </div>
              </div>
              <button onClick={() => setShowProfile(null)}>
                <X size={20} />
              </button>
            </div>

            {showProfile.location && (
              <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                <MapPin size={14} />
                {showProfile.location}
              </div>
            )}

            {showProfile.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {showProfile.genres.map((genre, idx) => (
                  <span key={idx} className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37' }}>
                    {genre}
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mb-4 text-sm">
              <div>
                <span className="font-bold">{showProfile.followers_count || 0}</span>
                <span className="text-muted-foreground ml-1">Followers</span>
              </div>
              <div>
                <span className="font-bold">{showProfile.following_count || 0}</span>
                <span className="text-muted-foreground ml-1">Following</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-3 mb-4">
              {showProfile.instagram_url && (
                <a href={showProfile.instagram_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#D4AF37' }}>
                  Instagram
                </a>
              )}
              {showProfile.tiktok_url && (
                <a href={showProfile.tiktok_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#D4AF37' }}>
                  TikTok
                </a>
              )}
              {showProfile.soundcloud_url && (
                <a href={showProfile.soundcloud_url} target="_blank" rel="noopener noreferrer" className="text-sm hover:underline" style={{ color: '#D4AF37' }}>
                  SoundCloud
                </a>
              )}
            </div>

            {showProfile.id !== user?.id && (
              <Button 
                className="w-full rounded-full"
                onClick={() => handleFollow(showProfile.id)}
                style={{ background: showProfile.is_following ? 'transparent' : '#D4AF37', color: showProfile.is_following ? '#D4AF37' : '#141414', border: '1px solid #D4AF37' }}
              >
                {showProfile.is_following ? (
                  <><UserMinus size={16} className="mr-2" /> Unfollow</>
                ) : (
                  <><UserPlus size={16} className="mr-2" /> Follow</>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Profile Setup Modal */}
      {showProfileSetup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowProfileSetup(false)}>
          <div className="glass-panel rounded-2xl p-6 border max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Edit DJ Profile</h3>
              <button onClick={() => setShowProfileSetup(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>DJ Name</Label>
                <Input
                  value={profileForm.dj_name}
                  onChange={(e) => setProfileForm({ ...profileForm, dj_name: e.target.value })}
                  placeholder="Your DJ name"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Skill Level</Label>
                <select
                  value={profileForm.skill_level}
                  onChange={(e) => setProfileForm({ ...profileForm, skill_level: e.target.value })}
                  className="w-full mt-1 p-2 rounded-md border border-border bg-background"
                >
                  <option value="">Select level</option>
                  {SKILL_LEVELS.map((level) => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Preferred Genres</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DJ_GENRES.map((genre) => (
                    <button
                      key={genre}
                      type="button"
                      onClick={() => {
                        const newGenres = profileForm.genres.includes(genre)
                          ? profileForm.genres.filter(g => g !== genre)
                          : [...profileForm.genres, genre];
                        setProfileForm({ ...profileForm, genres: newGenres });
                      }}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        profileForm.genres.includes(genre)
                          ? 'bg-[#D4AF37]/20 border-[#D4AF37]'
                          : 'border-border hover:border-[#D4AF37]/50'
                      }`}
                      style={{ color: profileForm.genres.includes(genre) ? '#D4AF37' : '#a3a3a3' }}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Location (City / Country)</Label>
                <Input
                  value={profileForm.location}
                  onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                  placeholder="e.g., London, UK"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Instagram URL</Label>
                <Input
                  value={profileForm.instagram_url}
                  onChange={(e) => setProfileForm({ ...profileForm, instagram_url: e.target.value })}
                  placeholder="https://instagram.com/..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label>TikTok URL</Label>
                <Input
                  value={profileForm.tiktok_url}
                  onChange={(e) => setProfileForm({ ...profileForm, tiktok_url: e.target.value })}
                  placeholder="https://tiktok.com/@..."
                  className="mt-1"
                />
              </div>

              <div>
                <Label>SoundCloud URL</Label>
                <Input
                  value={profileForm.soundcloud_url}
                  onChange={(e) => setProfileForm({ ...profileForm, soundcloud_url: e.target.value })}
                  placeholder="https://soundcloud.com/..."
                  className="mt-1"
                />
              </div>

              <Button 
                className="w-full rounded-full mt-4"
                onClick={handleUpdateProfile}
                style={{ background: '#D4AF37', color: '#141414' }}
              >
                Save Profile
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, type: '', postId: '', commentId: '' })}
        onConfirm={deleteModal.type === 'post' ? confirmDeletePost : confirmDeleteComment}
        type={deleteModal.type}
      />

      {/* Mix Upload Modal */}
      <MixUploadModal
        isOpen={showMixUpload}
        onClose={() => setShowMixUpload(false)}
        onUpload={handleMixUpload}
        uploadLimits={mixUploadLimits}
      />

      {/* Track Share Modal */}
      <TrackShareModal
        isOpen={showTrackShareModal}
        onClose={() => setShowTrackShareModal(false)}
        onSubmit={handleShareTrack}
      />

      {/* Profile Audit Modal */}
      <ProfileAuditModal
        isOpen={showProfileAuditModal}
        onClose={() => setShowProfileAuditModal(false)}
        onSuccess={fetchPosts}
      />
    </div>
  );
};

export default Community;
